import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Question Validation Editor Component
 * 
 * Provides an interface for editing validation rules for multiple choice questions.
 * Allows users to create IF/THEN logic rules that constrain answer selections.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.validation - Current validation rules array
 * @param {Function} props.onChange - Callback when validation rules change
 * @param {Array} props.answers - Available answers for the question
 * @param {String} props.questionType - Type of the question
 * @param {Object} props.colors - Color scheme for styling
 */
const QuestionValidationEditor = ({ 
  validation = [], 
  onChange, 
  answers = [], 
  questionType = 'multiple',
  colors = {},
  isEditing = false
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);
  const [localValidation, setLocalValidation] = useState(validation);

  useEffect(() => {
    setLocalValidation(validation);
  }, [validation]);

  // Only show for multiple choice questions and in editing mode
  if (questionType !== 'multiple' && questionType !== 'pdca') {
    return null;
  }

  // Don't show in compilation/simulation mode
  if (!isEditing) {
    return null;
  }

  /**
   * Gets the display text for an answer
   */
  const getAnswerText = (answerId) => {
    const answer = answers.find(a => (a.answerId || a.id) === answerId);
    if (!answer) return answerId;
    
    // Handle localized text
    if (Array.isArray(answer.answerText)) {
      const localizedAnswer = answer.answerText.find(t => t.language === 'it') || answer.answerText[0];
      return localizedAnswer?.text || 'Testo non disponibile';
    }
    
    return answer.answerText || 'Testo non disponibile';
  };

  /**
   * Adds a new validation rule
   */
  const addRule = () => {
    const newRule = {
      source_answers: [],
      source_condition: 'any_of',
      dest_answers: [],
      dest_condition: 'excluded_all'
    };
    
    const updatedRules = [...localValidation, newRule];
    setLocalValidation(updatedRules);
    onChange(updatedRules);
  };

  /**
   * Removes a validation rule
   */
  const removeRule = (index) => {
    const updatedRules = localValidation.filter((_, i) => i !== index);
    setLocalValidation(updatedRules);
    onChange(updatedRules);
  };

  /**
   * Updates a validation rule
   */
  const updateRule = (index, field, value) => {
    const updatedRules = [...localValidation];
    updatedRules[index] = {
      ...updatedRules[index],
      [field]: value
    };
    setLocalValidation(updatedRules);
    onChange(updatedRules);
  };

  /**
   * Toggles answer selection in source/destination arrays
   */
  const toggleAnswerSelection = (ruleIndex, field, answerId) => {
    const currentAnswers = localValidation[ruleIndex][field] || [];
    const isSelected = currentAnswers.includes(answerId);
    
    let updatedAnswers;
    if (isSelected) {
      updatedAnswers = currentAnswers.filter(id => id !== answerId);
    } else {
      updatedAnswers = [...currentAnswers, answerId];
    }
    
    updateRule(ruleIndex, field, updatedAnswers);
  };

  return (
    <div className="col-12 px-0">
      <div className="card mb-3 w-100" style={{ backgroundColor: colors?.editContextQuestion?.body || '#f8f9fa' }}>
        <div 
          className="card-header d-flex justify-content-between align-items-center" 
          style={{ 
            backgroundColor: colors?.secondaryLight || '#e9ecef',
            cursor: 'pointer'
          }}
          onClick={() => setCollapsed(!collapsed)}
        >
          <div className="d-flex align-items-center">
            <i 
              className="fa-solid fa-shield-check me-2"
            ></i>
            <h6 className="mb-0">
              {t('validationRules', { ns: 'labels', defaultValue: 'Regole di Validazione' })}
              {localValidation.length > 0 && (
                <span className="badge bg-secondary ms-2">{localValidation.length}</span>
              )}
            </h6>
          </div>
          <i 
            className={`fa-solid ${collapsed ? 'fa-plus' : 'fa-minus'}`}
          ></i>
        </div>

        {!collapsed && (
          <div className="card-body">
            {/* Info box */}
            <div className="alert alert-info d-flex align-items-start mb-3">
              <i className="fa-solid fa-info-circle me-2 mt-1"></i>
              <div>
                <div className="fw-bold mb-1">
                  {t('validationRulesInfo', { 
                    ns: 'labels', 
                    defaultValue: 'Come funzionano le regole di validazione' 
                  })}
                </div>
                <small>
                  {t('validationRulesDescription', { 
                    ns: 'labels',
                    defaultValue: 'Le regole seguono la logica "SE/ALLORA" per controllare le combinazioni di risposte permesse. Ogni regola definisce una condizione di attivazione e un\'azione risultante.'
                  })}
                </small>
              </div>
            </div>

            {/* Rules list */}
            {localValidation.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <i className="fa-solid fa-shield-check fa-3x mb-3 d-block"></i>
                <p className="mb-0">
                  {t('noValidationRules', { 
                    ns: 'labels', 
                    defaultValue: 'Nessuna regola di validazione configurata' 
                  })}
                </p>
                <small>
                  {t('clickAddRule', { 
                    ns: 'labels', 
                    defaultValue: 'Clicca "Aggiungi Regola" per iniziare' 
                  })}
                </small>
              </div>
            ) : (
              localValidation.map((rule, index) => (
                <div key={index} className="validation-rule mb-4 p-3 border rounded">
                  {/* Rule header */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">
                      <i className="fa-solid fa-link me-2"></i>
                      {t('rule', { ns: 'labels', defaultValue: 'Regola' })} {index + 1}
                    </h6>
                    <button 
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeRule(index)}
                      title={t('removeRule', { ns: 'labels', defaultValue: 'Rimuovi regola' })}
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </div>

                  <div className="row">
                    {/* Source condition (IF) */}
                    <div className="col-md-6">
                      <div className="p-3 border rounded mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <h6 className="text-primary mb-3">
                          <i className="fa-solid fa-arrow-right me-1"></i>
                          {t('if', { ns: 'labels', defaultValue: 'SE' })}
                        </h6>
                        
                        {/* Source condition type */}
                        <div className="mb-3">
                          <label className="form-label small">
                            {t('sourceCondition', { ns: 'labels', defaultValue: 'Condizione di attivazione' })}
                          </label>
                          <select 
                            className="form-select form-select-sm"
                            value={rule.source_condition || 'any_of'}
                            onChange={(e) => updateRule(index, 'source_condition', e.target.value)}
                          >
                            <option value="any_of">
                              {t('anyOfThese', { ns: 'labels', defaultValue: 'Almeno una di queste' })}
                            </option>
                            <option value="all">
                              {t('allOfThese', { ns: 'labels', defaultValue: 'Tutte queste' })}
                            </option>
                            <option value="not_in">
                              {t('noneOfThese', { ns: 'labels', defaultValue: 'Nessuna di queste' })}
                            </option>
                          </select>
                        </div>

                        {/* Source answers */}
                        <div>
                          <label className="form-label small">
                            {t('sourceAnswers', { ns: 'labels', defaultValue: 'Risposte di attivazione' })}
                          </label>
                          <div className="answer-checkboxes">
                            {answers.map((answer) => {
                              const answerId = answer.answerId || answer.id;
                              const isSelected = (rule.source_answers || []).includes(answerId);
                              
                              return (
                                <div key={answerId} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`source-${index}-${answerId}`}
                                    checked={isSelected}
                                    onChange={() => toggleAnswerSelection(index, 'source_answers', answerId)}
                                  />
                                  <label 
                                    className="form-check-label small" 
                                    htmlFor={`source-${index}-${answerId}`}
                                  >
                                    {getAnswerText(answerId)}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Destination condition (THEN) */}
                    <div className="col-md-6">
                      <div className="p-3 border rounded mb-3" style={{ backgroundColor: '#f0f8f0' }}>
                        <h6 className="text-success mb-3">
                          <i className="fa-solid fa-arrow-left me-1"></i>
                          {t('then', { ns: 'labels', defaultValue: 'ALLORA' })}
                        </h6>
                        
                        {/* Destination condition type */}
                        <div className="mb-3">
                          <label className="form-label small">
                            {t('destCondition', { ns: 'labels', defaultValue: 'Azione risultante' })}
                          </label>
                          <select 
                            className="form-select form-select-sm"
                            value={rule.dest_condition || 'excluded_all'}
                            onChange={(e) => updateRule(index, 'dest_condition', e.target.value)}
                          >
                            <option value="mandatory_any">
                              {t('mustSelectOneOf', { ns: 'labels', defaultValue: 'Deve selezionare almeno una di' })}
                            </option>
                            <option value="mandatory_all">
                              {t('mustSelectAllOf', { ns: 'labels', defaultValue: 'Deve selezionare tutte' })}
                            </option>
                            <option value="excluded_all">
                              {t('cannotSelect', { ns: 'labels', defaultValue: 'Non può selezionare' })}
                            </option>
                          </select>
                        </div>

                        {/* Destination answers */}
                        <div>
                          <label className="form-label small">
                            {t('destAnswers', { ns: 'labels', defaultValue: 'Risposte interessate' })}
                          </label>
                          <div className="answer-checkboxes">
                            {answers.map((answer) => {
                              const answerId = answer.answerId || answer.id;
                              const isSelected = (rule.dest_answers || []).includes(answerId);
                              
                              return (
                                <div key={answerId} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`dest-${index}-${answerId}`}
                                    checked={isSelected}
                                    onChange={() => toggleAnswerSelection(index, 'dest_answers', answerId)}
                                  />
                                  <label 
                                    className="form-check-label small" 
                                    htmlFor={`dest-${index}-${answerId}`}
                                  >
                                    {getAnswerText(answerId)}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Rule preview */}
                  {rule.source_answers?.length > 0 && rule.dest_answers?.length > 0 && (
                    <div className="mt-3 p-2 bg-light border rounded">
                      <small className="text-muted">
                        <i className="fa-solid fa-eye me-1"></i>
                        <strong>{t('preview', { ns: 'labels', defaultValue: 'Anteprima' })}:</strong>
                        {' '}
                        {t('if', { ns: 'labels', defaultValue: 'Se' })}
                        {' '}
                        {t(rule.source_condition, { ns: 'labels', defaultValue: rule.source_condition })}
                        {' '}
                        <em>{rule.source_answers.map(getAnswerText).join(', ')}</em>
                        {' '}
                        {t('then', { ns: 'labels', defaultValue: 'allora' })}
                        {' '}
                        {t(rule.dest_condition, { ns: 'labels', defaultValue: rule.dest_condition })}
                        {' '}
                        <em>{rule.dest_answers.map(getAnswerText).join(', ')}</em>
                      </small>
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Add rule button */}
            <div className="text-center">
              <button 
                className="btn btn-outline-primary"
                onClick={addRule}
              >
                <i className="fa-solid fa-plus me-2"></i>
                {t('addRule', { ns: 'labels', defaultValue: 'Aggiungi Regola' })}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionValidationEditor;
